import { Router, ScreenBase } from "@frui.ts/screens";
import LocalizationService from "../../services/localizationService";
import { action, computed, observable, reaction, runInAction, toJS } from "mobx";
import AppRouter from "services/appRouter";
import SummaryViewModel from "./summaryMultiEshopViewModel";
import Enum from "../../models/enum";
import EnumService from "../../services/enumService";
import ChargeRegistrationService from "../../services/chargeRegistrationService";
import EventBus, { vehicleChargeOrderInstructions, vehicleChargeValidityInstructions } from "../../services/eventBus";
import { getStatusSteps } from "./multiEshopStatusSteps";
import BatchOrderViewModel from "./batchOrderViewModel";
import ChargeOrderService from "services/chargeOrderService";
import ChargeOrderItem from "models/chargeOrderItem";
import VehicleService from "services/vehicleService";
import ChargeValidityInstructionGroupsResponse from "entities/chargeValidityInstructionGroupsResponse";
import ChargeOrderInstructionGroupsResponse from "entities/chargeOrderInstructionGroupsResponse";
import FuelTypeResponse from "entities/fuelTypeResponse";
import ChargeValidityInstructionGroupResponse from "entities/chargeValidityInstructionGroupResponse";
import ChargeOverlapType from "entities/chargeOverlapType";
import ChargeOrderInstructionType from "entities/chargeOrderInstructionType";
import ChargeVehicleWarningReasonOrder from "entities/chargeVehicleWarningReasonOrder";
import ChargeTypesService from "services/chargeTypesService";
import ExemptionReasonResponse from "entities/exemptionReasonResponse";
import { isMobile } from "react-device-detect";
import { scrollToElement } from "utils/scrollTo";
import ChargeValidityOverlapInstructionsGroupResponse from "entities/chargeValidityOverlapInstructionsGroupResponse";
import { IVehicle } from "models/types";
import FuelTypeLimitationResponse from "entities/fuelTypeLimitationResponse";
import FuelTypeLimitationReasonOrder from "entities/fuelTypeLimitationReasonOrder";
import FuelTypeLimitationReason from "entities/fuelTypeLimitationReason";
import TimeService from "services/timeService";
import { dateToIsoDate, endOfCzechDay, isSamePragueDay } from "utils/dateUtils";

export const OrderRecapViewModelNavigationName = "recap";

export enum OverlayValidity {
  VALID = "VALID",
}

class ChargeOverlay {
  licensePlate: string;
  state: OverlayValidity;
  expiration: Date;
  selected? = false;
}
class ChargeExemptions {
  instructionType: string;
  possibleExemptionId?: string;
  possibleExemptionReason?: string;
  vehicles?: IVehicle[];
}

class ChargePossibleExemptions {
  instructionType: string;
  possibleExemptionId?: string;
  fuelTypeLimitation: FuelTypeLimitationResponse;
  possibleExemptionReason?: string;
  fuelTypeLable: string;
  vehicles?: IVehicle[];
}
class CountryData {
  [key: string]: IVehicle[];
}

@Router.registerRoute({ name: "recap", route: OrderRecapViewModelNavigationName })
export default class OrderRecapViewModel extends ScreenBase {
  private unsubscription: Array<() => void> = [];

  @observable detail?: ChargeOrderItem = undefined;
  @observable detailIndex = -1;
  @observable loading: boolean;
  @observable summaryViewModel: SummaryViewModel;
  @observable possibleExemptionIds: string[] = [];
  @observable overlayRecommendationHidden = false;
  @observable overlayExemptionRecommendationHidden = false;

  @observable exemptions: ChargeExemptions[] = [];
  @observable exemptionsIdentifiedPossible: ChargeExemptions[] = [];
  @observable possibleExemptions: ChargePossibleExemptions[] = [];
  @observable overlays: ChargeOverlay[] = [];
  @observable overlapsExemption: ChargeValidityOverlapInstructionsGroupResponse[] = [];
  @observable overlaysTitle: string;
  @observable allOverlaysSelected: boolean | null = false;

  // Flag to signal that fuel type limitations should be processed in event handler
  private shouldProcessFuelTypeLimitations = false;

  statusNavigation = getStatusSteps();

  protected subscriptions: Array<() => void> = [];

  constructor(
    public localizationService: LocalizationService,
    public chargeRegistrationService: ChargeRegistrationService,
    public timeService: TimeService,
    public vehicleService: VehicleService,
    private chargeOrderService: ChargeOrderService,
    private chargeTypesService: ChargeTypesService,
    private summaryViewModelFactory: ReturnType<typeof SummaryViewModel.Factory>,
    private enumService: EnumService,
    private router: AppRouter,
    protected eventBus: EventBus
  ) {
    super();
    this.navigationName = OrderRecapViewModelNavigationName;
    this.subscriptions.push(this.eventBus.subscribe(vehicleChargeOrderInstructions, this.vehicleChargeOrderInstructions));
    this.subscriptions.push(this.eventBus.subscribe(vehicleChargeValidityInstructions, this.vehicleChargeValidityInstructions));
  }

  protected async onInitialize(): Promise<any> {
    await this.enumService.lazyValues("countries");
    this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    this.loading = true;
    this.summaryViewModel = this.summaryViewModelFactory(false, this.loading, this.onContinue, this.onBack);
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.subscriptions.forEach(unsubscribe => unsubscribe());
      this.subscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected async onActivate(): Promise<any> {
    if (isMobile && this.stepper) {
      scrollToElement(this.stepper);
    }
    this.loading = true;
    this.unsubscription.push(reaction(() => this.loading, this.onLoadingChange));

    const isChargesModified = this.chargeOrderService.chargeOrder.charges.some(charge => charge._isModified);

    if (isChargesModified) {
      this.resetObservables();
      this.chargeOrderService.chargeOrder.charges.forEach(charge => (charge._isModified = false));
      this.chargeOrderService.persistChargeOrder();
    }

    // Always call BE to get fresh data - loadVehicleInformation resets data before loading
    await this.loadVehicleInformation(isChargesModified);

    return super.onActivate();
  }

  private async goBackIfNeeded() {
    if (toJS(this.chargeOrderService.chargeOrder.charges).flatMap(item => item.licensePlates).length === 0)
      await this.router.navigate(BatchOrderViewModel);
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @computed
  get emptyState() {
    return this.localizationService.translateGeneral("eshop.order.summary.empty");
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get chargeOrderCharges() {
    return this.chargeOrderService.chargeOrder.charges;
  }

  @computed
  get translateVehicleCount() {
    return this.chargeOrderService.translateVehicleCount;
  }

  @computed
  get translateChargeLabel() {
    return this.chargeOrderService.chargeLabel;
  }

  @computed
  get exemptionRecommendationVisible() {
    return this.exemptionLicensePlates.length > 0;
  }

  @computed
  get possibleExemptionVisible() {
    return this.possibleExemption.length > 0;
  }

  @computed
  get overlayExemptionVisible() {
    // Don't show separate EXEMPTION_OVERLAP box if we already have VEHICLE_REGISTER_FUEL_TYPE_BASED_EXEMPTION
    // (they have the same message and will be merged in exemptionLicensePlates)
    const hasVehicleRegisterExemption = this.exemptions.some(
      e => e.instructionType === ChargeOrderInstructionType.VehicleRegisterFuelTypeBasedExemption
    );
    if (hasVehicleRegisterExemption) {
      return false;
    }
    return this.overlapsExemption?.length > 0 && !this.overlayExemptionRecommendationHidden;
  }

  @computed
  get overlayVisible() {
    return this.overlays.length > 0;
  }

  @computed
  get exemptionLicensePlates(): ChargeExemptions[] {
    // Merge vehicles from overlapsExemption into VEHICLE_REGISTER_FUEL_TYPE_BASED_EXEMPTION
    // to avoid duplicate boxes with same message
    let result = this.exemptions.map(exemption => {
      if (exemption.instructionType === ChargeOrderInstructionType.VehicleRegisterFuelTypeBasedExemption) {
        const overlapVehicles: IVehicle[] =
          this.overlapsExemption?.map(o => ({
            licensePlate: o.vehicle.licensePlate,
            countryId: o.vehicle.countryId,
          })) ?? [];
        const existingPlates = new Set(exemption.vehicles?.map(v => v.licensePlate) ?? []);
        const uniqueOverlapVehicles = overlapVehicles.filter(v => !existingPlates.has(v.licensePlate));
        return {
          ...exemption,
          vehicles: [...(exemption.vehicles ?? []), ...uniqueOverlapVehicles],
        };
      }
      return exemption;
    });

    // Merge VEHICLE_REGISTER_VERIFICATION_DISABLED into VEHICLE_UNVERIFIED_WITH_VEHICLE_REGISTER
    // as they have the same message
    const unverifiedExemption = result.find(
      e => e.instructionType === ChargeOrderInstructionType.VehicleUnverifiedWithVehicleRegister
    );
    const disabledExemption = result.find(
      e => e.instructionType === ChargeOrderInstructionType.VehicleRegisterVerificationDisabled
    );

    if (unverifiedExemption && disabledExemption) {
      // Merge disabled vehicles into unverified, avoiding duplicates
      const existingPlates = new Set(unverifiedExemption.vehicles?.map(v => v.licensePlate) ?? []);
      const uniqueDisabledVehicles = (disabledExemption.vehicles ?? []).filter(v => !existingPlates.has(v.licensePlate));
      unverifiedExemption.vehicles = [...(unverifiedExemption.vehicles ?? []), ...uniqueDisabledVehicles];
      // Remove the disabled exemption from results
      result = result.filter(e => e.instructionType !== ChargeOrderInstructionType.VehicleRegisterVerificationDisabled);
    }

    return result;
  }

  @computed
  get exemptionIdentifiedPossibleLicensePlate(): ChargeExemptions[] {
    return this.exemptionsIdentifiedPossible;
  }

  @computed
  get possibleExemption() {
    return this.possibleExemptions;
  }

  @computed
  get overlaysRemoveDisabled() {
    return !(this.overlays.filter(overlay => overlay.selected !== null && overlay.selected).length > 0);
  }

  @computed
  get countRecommendation() {
    return (
      (this.overlayRecommendationVisible ? 1 : 0) +
      (this.exemptionRecommendationVisible ? 1 : 0) +
      (this.overlayExemptionVisible ? 1 : 0) +
      (this.possibleExemptionVisible ? 1 : 0)
    );
  }

  @computed
  get overlayRecommendationVisible() {
    return this.overlayLicensePlates.length > 0 && !this.overlayRecommendationHidden;
  }

  @computed
  get overlayLicensePlates(): ChargeOverlay[] {
    return this.overlays;
  }

  @action.bound
  onLoadingChange() {
    this.summaryViewModel = this.summaryViewModelFactory(false, this.loading, this.onContinue, this.onBack);
  }

  @action.bound
  resetObservables() {
    this.possibleExemptionIds = [];
    this.overlayRecommendationHidden = false;
    this.overlayExemptionRecommendationHidden = false;
    this.allOverlaysSelected = false;
    this.overlays = [];
    this.exemptions = [];
    this.exemptionsIdentifiedPossible = [];
    this.overlapsExemption = [];
    this.possibleExemptions = [];
  }

  @action.bound
  getMappedExemptions(instructions: any): ChargeExemptions[] {
    return Object.keys(instructions).flatMap(key => {
      if (key === ChargeOrderInstructionType.IdentifiedPossibleExemption) {
        return instructions[key].map((i: any) => {
          return {
            instructionType: key,
            possibleExemptionId: Math.random().toString(),
            possibleExemptionReason: this.exemptionReasonWarning(i.possibleExemptionReasonIds),
            vehicles: i.vehicles.map((v: IVehicle) => v),
          };
        });
      }
      return {
        instructionType: key,
        possibleExemptionId: Math.random().toString(),
        vehicles: instructions[key].flatMap((i: any) => i.vehicles.flatMap((v: IVehicle) => v)),
      };
    });
  }

  @action.bound
  translateFuelTypeLimitationLink(code: string) {
    if (code === FuelTypeLimitationReason.DerivedFromVehicleRegisterData) {
      return {
        text: this.translateWithLink("possibleExemptions", "REGISTER_LINK"),
        link: this.translateWithLink("possibleExemptions", "REGISTER_LINK_URL"),
      };
    }
    return {
      text: this.translateWithLink("possibleExemptions", "ECO_LINK"),
      link: this.translateWithLink("possibleExemptions", "ECO_LINK_URL"),
    };
  }

  @action.bound
  translateFuelTypeLimitation(code: string) {
    return this.translateWithLink("possibleExemptions", code);
  }

  @action.bound
  translateWithLink(code: string, secondCode?: string) {
    return this.localizationService.translateGeneral(
      secondCode ? `multiEshop.${code}.${secondCode}` : `eshop.order.${this.navigationName}.${code}`,
      undefined,
      true // Important for markdown links to function correctly
    );
  }
  @action.bound
  getMappedPossibleExemptions(instructions: any): ChargePossibleExemptions[] {
    return Object.keys(instructions).flatMap(key => {
      return instructions[key].map((i: any) => {
        return {
          possibleExemptionId: Math.random().toString(),
          fuelTypeLimitation: i.fuelTypeLimitation,
          possibleExemptionReason: i.fuelTypeLimitation?.limitationReason,
          fuelTypeLable: this.translateFuelLabel(i.fuelTypeLimitation?.allowedFuelTypeId),
          vehicles: i.vehicles,
        };
      });
    });
  }

  @action.bound translate(code: string) {
    return this.localizationService.translateGeneral(`multiEshop.${code}`);
  }

  @action.bound formatPrice(price: number) {
    return this.localizationService.formatPrice(price);
  }

  @action.bound translateFuelLabel(fuelTypeId: string) {
    const fuelTypes = this.enumService.values("fuel_types") as FuelTypeResponse[];
    const fuelType = fuelTypes.find(t => t.id.toString() === fuelTypeId)?.fuelType;
    if (!fuelType) {
      return false;
    }
    return this.localizationService.translateAttribute("fuelType", fuelType);
  }

  @action.bound
  public onContinue() {
    this.chargeOrderService.persistChargeOrder();
    this.router.navigate("contact");
  }

  @action.bound
  public onBack() {
    // Clear processed flag so charges can be re-processed if user comes back
    sessionStorage.removeItem("recap_charges_processed");
    this.router.navigate(BatchOrderViewModel);
  }

  @action.bound
  hideOverlayRecommendation = () => {
    this.overlayRecommendationHidden = true;
  };

  @action.bound
  hideExemptionOverlayRecommendation = () => {
    this.overlayExemptionRecommendationHidden = true;
  };

  @action.bound
  showDetail(item?: ChargeOrderItem, index = -1) {
    this.detail = item;
    if (this.detailIndex === index) {
      this.detailIndex = -1;
    } else {
      this.detailIndex = index;
    }
    return false;
  }

  @action.bound
  selectAllOverlays(idOverlay: string) {
    this.overlays.forEach(overlay => (overlay.selected = !overlay.selected));
  }

  @action.bound
  select = () => {
    this.allOverlaysSelected = this.overlays.reduce(
      (summary: boolean, overlay: ChargeOverlay) => summary && (overlay.selected || false),
      true
    );

    if (
      !this.allOverlaysSelected &&
      this.overlays.reduce((summary: boolean, overlay: ChargeOverlay) => summary || overlay.selected || false, false)
    ) {
      this.allOverlaysSelected = null;
    }
  };

  @action.bound
  hideExemptionRecommendation = (id?: string) => {
    if (!id) return;
    // If hiding VEHICLE_REGISTER_FUEL_TYPE_BASED_EXEMPTION, also clear overlapsExemption
    // since those vehicles are merged into this exemption box
    const exemptionToHide = this.exemptions.find(ex => ex.possibleExemptionId === id);
    if (exemptionToHide?.instructionType === ChargeOrderInstructionType.VehicleRegisterFuelTypeBasedExemption) {
      this.overlapsExemption = [];
    }
    this.exemptions = this.exemptions.filter(exemption => exemption.possibleExemptionId !== id);
  };

  @action.bound
  hideIdentifiedPossibleRecommendation = (id?: string) => {
    if (!id) return;
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible.filter(
      exemption => exemption.possibleExemptionId !== id
    );
  };

  @action.bound
  removeBatch = async (charge: ChargeOrderItem) => {
    const licensePlates = charge.licensePlates?.map(vehicle => vehicle);
    if (!licensePlates) return;
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      ?.map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(licensePlate => !licensePlates.includes(licensePlate));
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    this.overlays = this.overlays?.filter(overlay => !licensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(overlay => !licensePlates.includes(overlay.vehicle.licensePlate));
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.possibleExemptions = this.possibleExemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);

    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeExemptionsFromOrderItems = async (exemption: ChargeExemptions) => {
    const licensePlates = exemption.vehicles?.map(vehicle => vehicle.licensePlate);
    if (!licensePlates) return;
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      ?.map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(licensePlate => !licensePlates.includes(licensePlate));
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    this.overlays = this.overlays?.filter(overlay => !licensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(overlay => !licensePlates.includes(overlay.vehicle.licensePlate));
    this.exemptions = this.exemptions?.filter(ex => ex.possibleExemptionId !== exemption.possibleExemptionId);
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0)
      .filter(ex => ex.possibleExemptionId !== exemption.possibleExemptionId);
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeIdentifiedPossibleFromOrderItems = async (identifiedPossible: ChargeExemptions) => {
    const licensePlates = identifiedPossible.vehicles?.map(vehicle => vehicle.licensePlate);
    if (!licensePlates) return;
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      ?.map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(licensePlate => !licensePlates.includes(licensePlate));
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    this.overlays = this.overlays?.filter(overlay => !licensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(overlay => !licensePlates.includes(overlay.vehicle.licensePlate));

    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !licensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);

    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible?.filter(
      ex => ex.possibleExemptionId !== identifiedPossible.possibleExemptionId
    );

    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeOverlaysFromOrderItems = async () => {
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      .map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(
          licensePlate =>
            !toJS(this.overlayLicensePlates.filter(overlay => overlay.selected))
              .flatMap(overlay => overlay.licensePlate)
              .includes(licensePlate)
        );
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    const selectedLicensePlates = this.overlays?.filter(o => o.selected).map(s => s.licensePlate);
    this.overlays = this.overlays?.filter(overlay => !selectedLicensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(
      overlay => !selectedLicensePlates.includes(overlay.vehicle.licensePlate)
    );
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  removeExemptionOverlaysFromOrderItems = async () => {
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges
      .map(orderItem => {
        orderItem.licensePlates = orderItem.licensePlates.filter(
          licensePlate =>
            !toJS(this.overlapsExemption.filter(overlay => overlay.vehicle.licensePlate))
              .flatMap(overlay => overlay.vehicle.licensePlate)
              .includes(licensePlate)
        );
        return orderItem;
      })
      .filter(item => item.licensePlates && item.licensePlates.length !== 0);

    const selectedLicensePlates = this.overlapsExemption?.map(s => s.vehicle.licensePlate);
    this.overlays = this.overlays?.filter(overlay => !selectedLicensePlates.includes(overlay.licensePlate));
    this.overlapsExemption = this.overlapsExemption?.filter(
      overlay => !selectedLicensePlates.includes(overlay.vehicle.licensePlate)
    );
    this.exemptions = this.exemptions
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.exemptionsIdentifiedPossible = this.exemptionsIdentifiedPossible
      ?.map(ex => {
        ex.vehicles = ex.vehicles?.filter(vehicle => !selectedLicensePlates.includes(vehicle.licensePlate ?? ""));
        return ex;
      })
      .filter(ex => ex.vehicles?.length !== 0);
    this.chargeOrderService.persistChargeOrder();

    await this.goBackIfNeeded();
  };

  @action.bound
  exemptionReasonWarning(possibleExemptionReasonIds: string[]): string {
    if (!possibleExemptionReasonIds?.length) {
      return this.localizationService.translateGeneral("errors.generic");
    }
    const possibleExemptionReasonId = possibleExemptionReasonIds[0];
    const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    const matchingReason = exemptionReasons.find(x => x.id === possibleExemptionReasonId);
    switch (matchingReason?.exemptionReason) {
      case "C":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_C";
      case "P":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_P";
      case "O":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_O";
      case "O1":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_O";
      case "O2":
        return ChargeOrderInstructionType.IdentifiedPossibleExemption + "_O";
      default:
        return this.localizationService.translateGeneral("errors.generic");
    }
  }

  @action.bound
  groupByOverlapTypes(data: any) {
    return data.reduce((acc: any, obj: any) => {
      obj.overlapTypes.forEach((type: any) => {
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(obj);
      });
      return acc;
    }, {});
  }

  @action.bound
  groupByInstructionTypeType(data: any) {
    return data.reduce((acc: any, obj: any) => {
      const key = obj.instructionType;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  @action.bound
  groupByPossibleExemptionReason(data: any) {
    return data.reduce((acc: any, obj: any) => {
      const key = obj.possibleExemptionReason;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  @action.bound
  groupByCountry(data: any) {
    return data.reduce((acc: any, obj: any) => {
      const key = obj.countryId;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  @action.bound
  filterByInstructionType(groupedInstructions: any, isFuelTypeLimitation = false) {
    return Object.keys(groupedInstructions)
      .filter(instructionType =>
        isFuelTypeLimitation
          ? instructionType === ChargeOrderInstructionType.IdentifiedFuelTypeLimitation
          : instructionType !== ChargeOrderInstructionType.IdentifiedFuelTypeLimitation
      )
      .reduce((acc: any, instructionType) => {
        acc[instructionType] = groupedInstructions[instructionType];
        return acc;
      }, {});
  }

  @action.bound
  filterIdentifiedPossible(groupedInstructions: any, isIdentifiedPossible = false) {
    return Object.keys(groupedInstructions)
      .filter(instructionType =>
        isIdentifiedPossible
          ? instructionType === ChargeOrderInstructionType.IdentifiedPossibleExemption
          : instructionType !== ChargeOrderInstructionType.IdentifiedPossibleExemption
      )
      .reduce((acc: any, instructionType) => {
        acc[instructionType] = groupedInstructions[instructionType];
        return acc;
      }, {});
  }

  @action.bound
  protected vehicleChargeOrderInstructions(data: {
    type: string;
    payload: { chargeOrderInstructionGroups?: ChargeOrderInstructionGroupsResponse };
  }) {
    const instructions = data.payload.chargeOrderInstructionGroups?.items;
    if (!instructions) return;

    // Reset to prevent duplicates - BE returns all instructions at once
    this.exemptions = [];
    this.exemptionsIdentifiedPossible = [];
    this.possibleExemptions = [];

    const groupedInstructions: any = this.groupByInstructionTypeType(instructions);
    const exemptions = this.filterIdentifiedPossible(this.filterByInstructionType(groupedInstructions));
    const exemptionsIdentifiedPossible = this.filterIdentifiedPossible(this.filterByInstructionType(groupedInstructions), true);

    const possibleExemptions = this.filterByInstructionType(groupedInstructions, true);
    const exemptionsMapped: ChargeExemptions[] = this.getMappedExemptions(exemptions);
    const exemptionsMappedIdentifiedPossible: ChargeExemptions[] = this.getMappedExemptions(exemptionsIdentifiedPossible);
    const possibleExemptionsMapped: ChargePossibleExemptions[] = this.getMappedPossibleExemptions(possibleExemptions);

    if (exemptionsMapped) {
      this.exemptions = exemptionsMapped.sort((a, b) => {
        return (
          ChargeVehicleWarningReasonOrder[a.instructionType as keyof ChargeVehicleWarningReasonOrder] -
          ChargeVehicleWarningReasonOrder[b.instructionType as keyof ChargeVehicleWarningReasonOrder]
        );
      });
    }
    if (exemptionsMappedIdentifiedPossible) {
      this.exemptionsIdentifiedPossible = this.getMappedExemptions(
        this.groupByPossibleExemptionReason(exemptionsMappedIdentifiedPossible)
      ).sort((a, b) => {
        return (
          ChargeVehicleWarningReasonOrder[a.instructionType as keyof ChargeVehicleWarningReasonOrder] -
          ChargeVehicleWarningReasonOrder[b.instructionType as keyof ChargeVehicleWarningReasonOrder]
        );
      });
    }
    if (possibleExemptionsMapped) {
      this.possibleExemptions = possibleExemptionsMapped;
      this.possibleExemptions = this.possibleExemptions.sort((a, b) => {
        return (
          FuelTypeLimitationReasonOrder[a.fuelTypeLimitation?.limitationReason as keyof FuelTypeLimitationReasonOrder] -
          FuelTypeLimitationReasonOrder[a.fuelTypeLimitation?.limitationReason as keyof FuelTypeLimitationReasonOrder]
        );
      });

      // Process fuel type limitations immediately while we have the data
      // Check flag set by loadVehicleInformation
      if (this.shouldProcessFuelTypeLimitations) {
        this.processFuelTypeLimitations();
        this.shouldProcessFuelTypeLimitations = false;
      }
    }
  }

  @action.bound
  processFuelTypeLimitations() {
    if (!this.possibleExemptions?.length) return;

    // Collect all vehicles that need fuel type change with their target properties
    // Key: "allowedFuelTypeId|countryId|chargeTypeId|validSince" -> vehicles and their original charges
    const vehiclesToMove: Array<{
      licensePlate: string;
      allowedFuelTypeId: string;
      originalCharge: ChargeOrderItem;
    }> = [];

    for (const exemption of this.possibleExemptions) {
      if (!exemption.fuelTypeLimitation?.allowedFuelTypeId) continue;
      const allowedFuelTypeId = exemption.fuelTypeLimitation.allowedFuelTypeId;

      for (const vehicle of exemption.vehicles || []) {
        if (!vehicle.licensePlate) continue;

        const originalCharge = this.chargeOrderCharges.find(
          charge =>
            charge.licensePlates.includes(vehicle.licensePlate!) &&
            vehicle.countryId === charge.countryId &&
            charge.fuelTypeId !== allowedFuelTypeId
        );

        if (!originalCharge) continue;

        // Check if already added (avoid duplicates)
        if (!vehiclesToMove.some(v => v.licensePlate === vehicle.licensePlate)) {
          vehiclesToMove.push({
            licensePlate: vehicle.licensePlate,
            allowedFuelTypeId,
            originalCharge,
          });
        }
      }
    }

    // Group vehicles by original charge to check if ALL vehicles in a charge need to change
    const vehiclesByOriginalCharge = new Map<ChargeOrderItem, typeof vehiclesToMove>();
    for (const v of vehiclesToMove) {
      if (!vehiclesByOriginalCharge.has(v.originalCharge)) {
        vehiclesByOriginalCharge.set(v.originalCharge, []);
      }
      vehiclesByOriginalCharge.get(v.originalCharge)!.push(v);
    }

    // First pass: If ALL vehicles in a charge need same fuel type, just change the charge
    const vehiclesToMoveToNewCharge: typeof vehiclesToMove = [];
    for (const [originalCharge, vehicles] of vehiclesByOriginalCharge) {
      const allSameFuelType = vehicles.every(v => v.allowedFuelTypeId === vehicles[0].allowedFuelTypeId);
      if (allSameFuelType && vehicles.length === originalCharge.licensePlates.length) {
        // All vehicles need same change - change the charge fuel type
        const allowedFuelTypeId = vehicles[0].allowedFuelTypeId;
        originalCharge.fuelTypeId = originalCharge._fuelTypeId = allowedFuelTypeId;
        originalCharge._priceListItems = this.chargeTypesService.chargePriceList?.items.filter(
          item => item.fuelTypeId === originalCharge.fuelTypeId
        );
        originalCharge.priceListItemId =
          originalCharge._priceListItems?.find(
            item => item.fuelTypeId === allowedFuelTypeId && item.chargeTypeId === originalCharge.chargeTypeId
          )?.id ?? "";
      } else {
        // Only some vehicles need to move
        vehiclesToMoveToNewCharge.push(...vehicles);
      }
    }

    // Second pass: Move vehicles to existing or new charges
    // Track newly created charges by key: "allowedFuelTypeId|countryId|chargeTypeId|validSinceDate"
    const newChargesMap = new Map<string, ChargeOrderItem>();

    for (const { licensePlate, allowedFuelTypeId, originalCharge } of vehiclesToMoveToNewCharge) {
      const hasImmediateOriginal = originalCharge._hasImmediateValidity;
      const validSinceKey = hasImmediateOriginal
        ? "immediate"
        : originalCharge.validSince
        ? dateToIsoDate(originalCharge.validSince)
        : "none";
      const targetKey = `${allowedFuelTypeId}|${originalCharge.countryId}|${originalCharge.chargeTypeId}|${validSinceKey}`;

      // Find existing charge with correct properties - must match fuel type, country, charge type, AND validity DATE
      let targetCharge = this.chargeOrderService.chargeOrder.charges.find(storedCharge => {
        if (storedCharge === originalCharge) return false;
        if (storedCharge.countryId !== originalCharge.countryId) return false;
        if (storedCharge.fuelTypeId !== allowedFuelTypeId) return false;
        if (storedCharge.chargeTypeId !== originalCharge.chargeTypeId) return false;

        // Check validity match - MUST compare actual dates, not just immediate flag
        const originalValidSince = originalCharge.validSince ?? originalCharge.validity?.validSinceDate;
        const storedValidSince = storedCharge.validSince ?? storedCharge.validity?.validSinceDate;

        // Both must have validSince and dates must match
        if (originalValidSince && storedValidSince) {
          return dateToIsoDate(originalValidSince) === dateToIsoDate(storedValidSince);
        }
        // Both undefined - match only if both have same immediate flag
        if (!originalValidSince && !storedValidSince) {
          return originalCharge._hasImmediateValidity === storedCharge._hasImmediateValidity;
        }
        return false;
      });

      // If not found, check our newly created charges
      if (!targetCharge) {
        targetCharge = newChargesMap.get(targetKey);
      }

      // Remove from original charge
      originalCharge.licensePlates = originalCharge.licensePlates?.filter(l => l !== licensePlate);

      if (targetCharge) {
        // Add to existing/new charge
        if (!targetCharge.licensePlates.includes(licensePlate)) {
          targetCharge.licensePlates = [...targetCharge.licensePlates, licensePlate];
        }
      } else {
        // Create new charge - copy validity from original charge
        const newCharge = this.chargeOrderService.addChargeOrderItem();
        newCharge.licensePlates = [licensePlate];
        newCharge.validity.hasImmediateValidity = newCharge._hasImmediateValidity = originalCharge._hasImmediateValidity;
        // Copy exact validSince from original - for immediate validity, use the current validSince value
        // (which should already be set to server time or will be updated by setChargeValidityByInstructions)
        newCharge.validity.validSinceDate = newCharge.validSince =
          originalCharge.validSince ?? originalCharge.validity?.validSinceDate;
        newCharge.validity._validUntilDate = newCharge._validUntil =
          originalCharge._validUntil ?? originalCharge.validity?._validUntilDate;
        newCharge._validUntilOverlap = originalCharge._validUntilOverlap ?? originalCharge.validity?._validUntilOverlap;
        newCharge.chargeTypeId = newCharge._chargeTypeId = originalCharge.chargeTypeId;
        newCharge.countryId = originalCharge.countryId;
        newCharge.fuelTypeId = newCharge._fuelTypeId = allowedFuelTypeId;
        newCharge._priceListItems = this.chargeTypesService.chargePriceList?.items.filter(
          item => item.fuelTypeId === newCharge.fuelTypeId
        );
        newCharge.priceListItemId =
          newCharge._priceListItems?.find(
            item => item.fuelTypeId === allowedFuelTypeId && item.chargeTypeId === newCharge.chargeTypeId
          )?.id ?? "";
        newCharge._allowedDelete = true;

        // Track this new charge for future vehicles with same target
        newChargesMap.set(targetKey, newCharge);
      }
    }

    // Clean up empty charges
    this.chargeOrderService.chargeOrder.charges = this.chargeOrderService.chargeOrder.charges.filter(
      item => item.licensePlates && item.licensePlates.length !== 0
    );
    this.chargeOrderService.persistStoredChargeOrder(this.chargeOrderService.chargeOrder);
  }
  @action.bound
  protected async vehicleChargeValidityInstructions(data: {
    type: string;
    payload: { chargeValidityInstructionGroups?: ChargeValidityInstructionGroupsResponse };
  }) {
    const response = data.payload.chargeValidityInstructionGroups;
    if (response?.validities) {
      await this.setChargeValidityByInstructions(response.validities);
    }

    if (response?.overlaps) {
      const groupedOverlaps = this.groupByOverlapTypes(response?.overlaps);
      const overlapsCharge = groupedOverlaps[ChargeOverlapType.ChargeOverlap] as ChargeValidityOverlapInstructionsGroupResponse[];
      this.overlapsExemption = groupedOverlaps[
        ChargeOverlapType.ExemptionOverlap
      ] as ChargeValidityOverlapInstructionsGroupResponse[];

      let overlays: ChargeOverlay[] = [];

      overlapsCharge?.forEach(o => {
        const charge = this.chargeOrderService.chargeOrder.charges.find(
          charge =>
            charge.priceListItemId === o.priceListItemId &&
            ((charge._hasImmediateValidity && o.requestedValidity.hasImmediateValidity) ||
              (!charge._hasImmediateValidity &&
                !o.requestedValidity.hasImmediateValidity &&
                o.requestedValidity.validSinceDate &&
                charge.validSince &&
                isSamePragueDay(new Date(o.requestedValidity.validSinceDate), charge.validSince)))
        );

        if (charge && o.overlappingCharges) {
          this.chargeOrderService.persistChargeOrder();
          const overlapPeriod = o.overlappingCharges.map(overlapPeriod => {
            return {
              licensePlate: o.vehicle.licensePlate ?? "",
              state: OverlayValidity.VALID,
              selected: false,
              expiration: overlapPeriod.validUntil,
            };
          });
          overlays = [...overlays, ...overlapPeriod];
        }
      });
      this.overlays = overlays;
    }
  }

  @action.bound
  protected async setChargeValidityByInstructions(validities: ChargeValidityInstructionGroupResponse[]) {
    // Update existing charges in place instead of replacing all with splice
    // This preserves any newly created charges from processFuelTypeLimitations
    for (const charge of this.chargeOrderService.chargeOrder.charges) {
      const match = validities.find(
        v =>
          charge.priceListItemId === v.priceListItemId &&
          ((charge._hasImmediateValidity && v.requestedValidity.hasImmediateValidity) ||
            (charge.validSince && dateToIsoDate(charge.validSince) === dateToIsoDate(v.validSince)))
      );
      if (match) {
        const nowOrSince = charge._hasImmediateValidity ? await this.timeService.getCurrentServerTime(true) : match.validSince;

        runInAction(() => {
          charge.validSince = nowOrSince;
          charge.validity.validSinceDate = nowOrSince;
          charge.validity._validUntilDate = endOfCzechDay(match.validUntil);
          charge.validity._validUntilOverlap = endOfCzechDay(match.validUntil);
          charge._validUntil = endOfCzechDay(match.validUntil);
          charge._validUntilOverlap = endOfCzechDay(match.validUntil);
        });
      } else if (charge._hasImmediateValidity && !charge.validSince) {
        // For new charges with immediate validity that don't have a match, set validSince to current server time
        const nowTime = await this.timeService.getCurrentServerTime(true);
        runInAction(() => {
          charge.validSince = nowTime;
          charge.validity.validSinceDate = nowTime;
        });
      }
    }

    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  resetChargeOrder() {
    // Clear processed flag so charges can be re-processed if user comes back
    sessionStorage.removeItem("recap_charges_processed");
    this.chargeOrderService.reset();
    this.router.navigate(BatchOrderViewModel);
  }

  @action.bound
  loadVehicleInformation = async (shouldProcess?: boolean) => {
    // Reset warnings before loading - prevents duplicates when navigating back
    this.exemptions = [];
    this.exemptionsIdentifiedPossible = [];
    this.possibleExemptions = [];
    this.overlays = [];
    this.overlapsExemption = [];

    if (this.chargeOrderService.chargeOrder.charges.length) {
      try {
        this.loading = true;

        // Set flag BEFORE BE call so event handler can process fuel type limitations
        this.shouldProcessFuelTypeLimitations = shouldProcess ?? false;

        // Call BE - event handler will process fuel type limitations when data arrives
        await this.vehicleService.getChargeOrderInstructions(this.chargeOrderService.chargeOrder.charges);

        // Now call validity instructions - this will work with updated charges
        await this.vehicleService.getChargeValidityInstructions(this.chargeOrderService.chargeOrder.charges);
      } catch (e) {
        // Silence catch of background process
      } finally {
        this.loading = false;
      }
    } else {
      this.loading = false;
    }
  };

  @action.bound
  toggleChargeOrderItem = (chargeOrderItem: ChargeOrderItem) => () => (chargeOrderItem._visible = !chargeOrderItem._visible);

  @action.bound
  chargePrice(item: ChargeOrderItem) {
    return this.chargeOrderService.chargePrice(item.chargeTypeId as string, item.fuelTypeId as string);
  }

  @action.bound
  formatValidFrom(item: ChargeOrderItem) {
    return item.validSince ? this.localizationService.formatDateTime(new Date(item.validSince)) : undefined;
  }

  @action.bound
  formatValidTo(item: ChargeOrderItem) {
    return item._validUntil ? this.localizationService.formatDateTime(new Date(item._validUntil)) : undefined;
  }

  @action.bound
  formatExpiration(date: Date) {
    return this.localizationService.formatDateTime(new Date(date));
  }

  @action.bound
  formatOverlayState(state: OverlayValidity) {
    return this.translate(`overlay.state.${state}`);
  }

  @action.bound
  getCountry(countryId: string) {
    const country = this.enumService.values("countries").find((country: Enum) => country.id === countryId);
    return !country ? "-" : country.name;
  }
}
